import { inject, Injectable } from '@angular/core';
import { Auth, authState } from '@angular/fire/auth';
import {
  doc,
  Firestore,
  serverTimestamp,
  setDoc,
} from '@angular/fire/firestore';
import { getToken, Messaging, onMessage } from '@angular/fire/messaging';
import { Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  afs = inject(Firestore);
  private auth: Auth = inject(Auth);
  private router = inject(Router);
  private messaging: Messaging = inject(Messaging);
  logger: NGXLogger = inject(NGXLogger);
  private toastr = inject(ToastrService);

  constructor() {}

  /**
   * Request permission and register the specific Firebase Service Worker
   */
  async initPushNotifications() {
    try {
      // 1. Build Dynamic URL for the Service Worker
      const config = environment.firebase;
      const swUrl =
        `firebase-messaging-sw.js?` +
        `apiKey=${config.apiKey}&` +
        `authDomain=${config.authDomain}&` +
        `projectId=${config.projectId}&` +
        `storageBucket=${config.storageBucket}&` +
        `messagingSenderId=${config.messagingSenderId}&` +
        `appId=${config.appId}`;

      // 2. Register SW
      const registration = await navigator.serviceWorker.register(swUrl, {
        type: 'classic',
        scope: '/',
      });

      // 3. Permission
      let permission = Notification.permission;
      if (permission === 'default') {
        permission = await Notification.requestPermission();
      }

      if (permission === 'granted') {
        const token = await getToken(this.messaging, {
          serviceWorkerRegistration: registration,
          vapidKey: environment.vapidKey, // Use key from environment
        });

        if (token) {
          this.logger.debug('FCM Web Token:', token);
          await this.saveTokenToDatabase(token);
        }
      }
    } catch (error) {
      this.logger.error('Push registration failed:', error);
    }
  }

  /**
   * Listen for messages while the app is actively open (Foreground)
   */
  listenForForegroundMessages() {
    onMessage(this.messaging, (payload) => {
      this.logger.log('Foreground message:', payload);

      const title = payload.notification?.title || 'New Notification';
      const body = payload.notification?.body || '';
      const link = payload.fcmOptions?.link || payload.data?.['link'];

      // Show a premium toast notification
      const activeToast = this.toastr.info(body, title, {
        timeOut: 8000,
        progressBar: true,
        closeButton: true,
        enableHtml: true,
      });

      if (link) {
        activeToast.onTap.subscribe(() => {
          this.router.navigateByUrl(link);
        });
      }
    });
  }

  private async saveTokenToDatabase(token: string) {
    this.logger.log('Saving user token to database...');
    const user =
      this.auth.currentUser || (await firstValueFrom(authState(this.auth)));
    if (user == null) {
      this.logger.warn('No authenticated user found. Cannot save token.');
      return;
    }
    const tokenRef = doc(
      this.afs,
      environment.FIRESTORE_USERS_COLLECTION,
      user.uid,
      environment.FIRESTORE_FCM_TOKENS_COLLECTION,
      token
    );
    return await setDoc(
      tokenRef,
      {
        token: token,
        uid: user.uid,
        platform: 'web',
        lastSeen: serverTimestamp(),
      },
      { merge: true }
    ).catch((error) => {
      this.logger.error('Error saving token to database:', error);
    });
  }
}
